import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, FolderPlus, Loader2 } from 'lucide-react';
import { driveApi, DriveFolderView } from '../services/driveApi';
import { errorText } from './ErrorBanner';
import { Project } from '../types/project';

/**
 * 드라이브 폴더 — 이 프로젝트의 사업 폴더와 단계 폴더(2026-10-22).
 * 폴더는 **사이트가 만든다.** 재무계획 · 제안서 · 수행으로 넘어가면 그 단계 폴더가 생기고,
 * 검토에서는 「폴더 만들기」를 누르거나 파일을 올릴 때 생긴다. DB 에는 폴더 id 만 있고 목록은 드라이브에서 읽는다.
 */
export const DriveFolderPanel: React.FC<{ project: Project }> = ({ project }) => {
  const [data, setData] = useState<DriveFolderView | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<'load' | 'ensure' | 'add' | null>('load');
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');

  const run = useCallback(async (kind: 'load' | 'ensure' | 'add', job: () => Promise<DriveFolderView>) => {
    setBusy(kind); setError(null);
    try { setData(await job()); return true; }
    catch (e) { setError(errorText(e, '드라이브 폴더를 처리하지 못했습니다')); return false; }
    finally { setBusy(null); }
  }, []);

  // 단계가 바뀌면 서버가 폴더를 만든다 — 다시 읽는다
  useEffect(() => { run('load', () => driveApi.get(project.id)); }, [project.id, project.stage, run]);

  const made = data?.folders.filter((f) => f.id).length ?? 0;
  const missing = data?.folders.filter((f) => !f.id && reached(project, f.from)).length ?? 0;

  return (
    <section className="bg-white rounded-lg border border-slate-200 mb-4">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-sm font-semibold text-slate-900">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} 드라이브 폴더
        </button>
        <span className="text-xs text-slate-500 min-w-0 truncate">
          {busy === 'load' && !data ? '불러오는 중...'
            : !data?.linked ? '아직 없음'
            : `${data.path.slice(1).join(' / ')} · 단계 폴더 ${made}개`}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {(!data?.linked || missing > 0) && (
            <button
              onClick={() => run('ensure', () => driveApi.ensure(project.id))}
              disabled={busy !== null}
              title="지금 단계까지의 표준 폴더를 만듭니다. 이미 있는 폴더는 그대로 둡니다"
              className="px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"
            >
              {busy === 'ensure' ? <Loader2 size={12} className="animate-spin" /> : <FolderPlus size={12} />}
              {data?.linked ? `빠진 폴더 만들기 (${missing})` : '폴더 만들기'}
            </button>
          )}
          {data?.url && (
            <a href={data.url} target="_blank" rel="noreferrer"
              className="px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1">
              드라이브에서 열기 <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
      {error && <p role="alert" className="mx-4 mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}

      {open && data?.linked && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-3">
          <ul className="grid gap-1 sm:grid-cols-2">
            {data.folders.map((f) => (
              <li key={f.kind} className="flex items-center gap-2 text-sm">
                {f.url
                  ? <a href={f.url} target="_blank" rel="noreferrer" className="text-slate-900 hover:text-indigo-600 hover:underline">{f.name}</a>
                  : <span className="text-slate-400">{f.name}</span>}
                <span className="text-xs text-slate-400">{f.id ? '' : reached(project, f.from) ? '없음' : `${f.from}부터`}</span>
              </li>
            ))}
            {data.others.map((f) => (
              <li key={f.id} className="flex items-center gap-2 text-sm">
                <a href={f.url} target="_blank" rel="noreferrer" className="text-slate-700 hover:text-indigo-600 hover:underline">{f.name}</a>
                <span className="text-xs text-slate-400">추가한 폴더</span>
              </li>
            ))}
          </ul>
          <form
            className="flex items-center gap-2"
            onSubmit={async (e) => {
              e.preventDefault();
              const n = name.trim();
              if (n && await run('add', () => driveApi.addFolder(project.id, n))) setName('');
            }}
          >
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="폴더 이름 (사업 폴더 바로 아래)"
              className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <button type="submit" disabled={busy !== null || !name.trim()}
              className="px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              {busy === 'add' ? <Loader2 size={14} className="animate-spin" /> : '폴더 추가'}
            </button>
          </form>
        </div>
      )}
    </section>
  );
};

const ORDER = ['검토', '재무계획', '제안서', '수행', '종료'];

/** 이 프로젝트가 그 폴더가 생기는 단계에 왔나. 실주 · 참여 안 함으로 끝난 건은 제안까지만 */
function reached(p: Project, from: string): boolean {
  const at = p.stage === '종료' && p.outcome !== '수주' ? ORDER.indexOf('제안서') : ORDER.indexOf(p.stage);
  return ORDER.indexOf(from) <= at;
}
